import clsx from 'clsx';
import { createContext, type ReactNode, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Divider } from '@flintmint/web-shared/components/divider';
import { Icon } from '@flintmint/web-shared/components/icon';
import { useTransitionMark } from '@flintmint/web-shared/hooks/use-transition-mark';

import { Logo } from '@/components/logo';

import { useModalLayer } from './use-modal-layer';

import styles from './full-screen-overlay.module.scss';

type FullScreenOverlayProps = {
  /** The h1. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** `wide` is the design's 1360 card (the plan picker), `medium` its 680 card (Recharge); the default is the 600 notice card. */
  size?: 'notice' | 'medium' | 'wide';
  /** A change of view, after which focus left stranded by an unmounted control moves to the h1. */
  view?: string;
  onEscape: () => void;
  /** Draws a close button in the card's top-right corner. */
  onClose?: () => void;
  /** Disables the close button, as while a step that must not be left is in progress. */
  closeDisabled?: boolean;
  children: ReactNode;
} & LoadingProps;

/** While loading the h1 is a skeleton, so the dialog is named by `loadingLabel` instead. */
type LoadingProps = { loading?: false; loadingLabel?: never } | { loading: boolean; loadingLabel: string };

/**
 * A centered card with the logo over a plain page covering every screen, which stays
 * mounted under it. A modal dialog labelled by its h1, above the floating chat (300) and level with modals (400);
 * toasts stay above it. It fades in and, inside a `FullScreenOverlayPresence`, out; its content fades in again
 * on each change of `view`. CSS transitions rather than motion: its WAAPI opacity/transform animations make
 * Chrome on macOS re-present stale frames.
 */
export const FullScreenOverlay = (props: FullScreenOverlayProps) => {
  const { title, subtitle, loading, loadingLabel, size = 'notice', view, onEscape, onClose, closeDisabled, children } = props;
  const titleId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const open = useContext(PresenceContext);
  useModalLayer(overlayRef, headingRef, onEscape, open);
  useTransitionMark(overlayRef, 'open', open);

  useEffect(() => {
    if (open && !overlayRef.current?.contains(document.activeElement)) headingRef.current?.focus();
  }, [view, open]);

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={loading ? undefined : titleId}
      aria-label={loading ? loadingLabel : undefined}
      aria-busy={loading || undefined}
      // Focusable, so a click on blank space keeps focus in the overlay.
      tabIndex={-1}
      // A closing overlay is gone for every user while it fades.
      inert={!open}
      aria-hidden={!open || undefined}
      className={styles.overlay}
    >
      <div className={clsx(styles.card, { [styles.medium]: size === 'medium', [styles.wide]: size === 'wide' })}>
        <div className={styles.header}>
          <Logo />
          <Divider orientation="horizontal" />
          {/* Keyed on the view, so a new view's heading and content mount again and fade in. */}
          <div key={view} className={clsx(styles.heading, styles.viewEnter)}>
            <h1 id={titleId} ref={headingRef} tabIndex={-1}>
              {title}
            </h1>
            {subtitle && <div>{subtitle}</div>}
          </div>
        </div>
        <div key={view} className={clsx(styles.viewContent, styles.viewEnter)}>
          {children}
        </div>
        {/* Last in the DOM, so Tab reaches the content first. */}
        {onClose && (
          <button type="button" aria-label="Close" className={styles.close} disabled={closeDisabled} onClick={onClose}>
            <Icon type="multiplication-sign" size={20} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

/** Whether the overlay is open rather than closing; an overlay outside a presence is always open. */
const PresenceContext = createContext(true);

// Matches the longest close transition in full-screen-overlay.module.scss.
const CLOSE_TRANSITION_MS = 200;

/**
 * Renders its overlay while `open` and keeps it mounted while it fades out after closing. The modal layer is
 * released as the close starts, so focus is back on the opener and the page is interactive during the fade.
 */
export const FullScreenOverlayPresence = ({ open, children }: { open: boolean; children: ReactNode }) => {
  const [wasOpen, setWasOpen] = useState(open);
  const [closing, setClosing] = useState(false);
  if (wasOpen !== open) {
    setWasOpen(open);
    setClosing(!open);
  }

  useEffect(() => {
    if (!closing) return;
    const timeout = setTimeout(() => setClosing(false), CLOSE_TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [closing]);

  return (open || closing) && <PresenceContext.Provider value={open}>{children}</PresenceContext.Provider>;
};

/** The notice panel: a bold title over muted rows, or a single plain message. */
export const OverlayPanel = ({ title, children }: { title?: string; children: ReactNode }) => (
  <div className={styles.panel}>
    {title && <strong>{title}</strong>}
    {title ? <div className={styles.panelRows}>{children}</div> : children}
  </div>
);

/** The small muted text under a notice panel. */
export const OverlayNote = ({ children }: { children: ReactNode }) => <div className={styles.note}>{children}</div>;
